import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { stationWebSocketClient } from '@utils/station';
import {
  shouldUseWebSocket,
  getWebSocketUrl,
  getAuthenticatedUserId,
  handleWebSocketMessage
} from '@utils/station';

/**
 * Hook to manage WebSocket connection for Napster Music UI
 * Only connects if:
 * 1. communication=websockets in URL
 * 2. Station mode is enabled
 * 3. User is authenticated
 */
export const useStationWebSocket = () => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldConnect, setShouldConnect] = useState(false);
  const hasInitialized = useRef(false);

  // Check if WebSocket should be used
  useEffect(() => {
    const useWS = shouldUseWebSocket();
    setShouldConnect(useWS);
  }, []);

  const userId = user?.profile ? getAuthenticatedUserId(user) : null;

  // Connect to WebSocket if conditions are met and user is authenticated
  useEffect(() => {
    if (!shouldConnect) return;

    // Wait for auth to complete
    if (!userId) {
      console.log('[Station WS] Waiting for authentication...');
      return;
    }

    if (hasInitialized.current) return;
    hasInitialized.current = true;

    console.log('[Station WS] Connecting with userId:', userId);

    // Connect to WebSocket
    stationWebSocketClient.connect({
      url: getWebSocketUrl(),
      userId,
      onConnected: () => {
        console.log('[Station WS] Connected successfully');
        setIsConnected(true);
        setError(null);
      },
      onDisconnected: () => {
        console.log('[Station WS] Disconnected');
        setIsConnected(false);
      },
      onMessage: (message) => {
        console.log('[Station WS] Message received:', message);
        // Handle the message by calling appropriate bridge functions
        handleWebSocketMessage(message);
      },
      onError: (err) => {
        console.error('[Station WS] Error:', err);
        setError(err);
      },
    });

    // Cleanup on unmount
    return () => {
      stationWebSocketClient.disconnect();
      hasInitialized.current = false;
    };
  }, [shouldConnect, userId]);

  /**
   * Send a message to other clients
   */
  const sendMessage = (type: string, payload?: any) => {
    if (!shouldConnect) {
      console.log('[Station WS] WebSocket not enabled, skipping message');
      return;
    }

    if (!isConnected) {
      console.warn('[Station WS] Not connected, cannot send message');
      return;
    }

    stationWebSocketClient.sendMessage(type, payload);
  };

  return {
    isEnabled: shouldConnect,
    isConnected,
    error,
    sendMessage,
  };
};
